/**
 * @file Project Sync Actions
 * @module admin/store/projectSyncActions
 * @desc Actions for syncing and comparing projects from Google Sheets with Firestore
 */

import api from 'src/store/api'
import { firecollection } from 'src/store/firebase'
import { fetchSheetData, writeSheetData } from 'src/utils/googleAuth'

const logger = console

/**
 * Maps spreadsheet columns to our system's project structure
 * @param {Array} row - Spreadsheet row values
 * @param {Array} headers - Spreadsheet column headers
 * @returns {Object} Mapped project data
 */
const mapSpreadsheetRow = (row, headers) => {
  const rowData = {}

  headers.forEach((header, index) => {
    rowData[header] = row[index] || ''
  })

  const mapped = {
    // Keep original spreadsheet data
    _spreadsheet: rowData,

    // Map to our system fields

    // Identifiers
    clientName: rowData.Client || '',
    projectId: rowData['Project ID'] || '',
    projectCode: rowData['Project Code'] || '',

    // Project details
    projectType: rowData['Project Type'] || '',
    projectStatus: rowData.Status || '',
    startDate: rowData['Project Start'] || null,
    launchDateEstimate: rowData['Projected Launch Date'] || null,
    launchDateActual: rowData['Actual Launch Date'] || null,
    endDate: rowData['Project Close'] || null,
    accountManager: rowData.AM || '',
    contractStatus: rowData['Contract Status'] || '',
    projectOwner: rowData['Project Owner'] || '',
    projectSupport: rowData['Project Support'] || '',
    scopingDoc: rowData['Scoping Doc'] || '',
    projectPlan: rowData['Project Plan'] || '',
    materials: rowData.Materials || '',
    totalHoursToDate: parseFloat(rowData['Total Hours To Date']) || 0,
    totalContractHours: parseFloat(rowData['Total Contract Hours']) || 0,
    percentHoursUtilized: rowData['% of Hours Utilized'] || '',
    notes: rowData.Notes || '',
  }

  return mapped
}

const getMatchByClientName = (args) => {
  const { isMissingClientName, sheetClientName, systemProjectsArray } = args

  if (isMissingClientName) {
    return null
  }

  // eslint-disable-next-line arrow-body-style
  const getPotentialClientNames = (name) => {
    return String(name).split(/[\s|,-]/).map((s) => s.trim()).filter((s) => s.length > 2)
  }

  // Find first project matching client name.
  return systemProjectsArray.find((p) => {
    const potentialClientNames = getPotentialClientNames(p.projectName)

    // Determine whether any items in the potentialClientNames array
    // match any part of the sheetClientName.
    return potentialClientNames.some((part) => sheetClientName.indexOf(part) !== -1)
  }) || null
}

const setupProjectInSystem = (projectInSystem = {}) => {
  /**
   * Set default values on flag properties.
   * These are updated later if matches are found.
   *
   * The view uses these flags to group projects and determine whether
   * a projectsInSheet lookup is needed to present both system and sheet
   * project details.
   *
   * @note Project Flags
   * Properties are `isInSystem`, `isInSheet`, and `isInSheetAndSystem`.
   * If new projectFlags are added to this projectsInSystem loop,
   * update the setting logic executed in the projectsInSheet loop below.
   */
  projectInSystem.isInSystem = true
  projectInSystem.isInSheet = false
  projectInSystem.isInSheetAndSystem = false

  return projectInSystem
}

const updateProjectInSystem = (projectInSystem = {}, updates = {}) => {
  Object.keys(updates).forEach((key) => {
    projectInSystem[key] = updates[key]
  })

  return projectInSystem
}

const createSystemProjectMap = (projectInSystem = {}, systemProjectMap = {}) => {
  // Index system project by projectId for quick lookup.
  if (projectInSystem.projectId) {
    systemProjectMap[projectInSystem.projectId] = projectInSystem
  } else if (projectInSystem.projectCode) {
    // Fallback to projectCode if no projectId
    systemProjectMap[projectInSystem.projectCode] = projectInSystem
  } else {
    // Do nothing. No valid identifier to index by.
  }

  return systemProjectMap
}

const buildResultOfComparison = (projectInSheet, overrides = {}) => {
  const defaultResult = {
    // Comparison info.
    matchBy: 'none',

    // Diff info.
    differences: [],
    totalDifferences: 0,

    // Source info.
    isInSheet: true,
    isInSystem: false,
    isInSheetAndSystem: false,

    // Matched properties.
    projectId: null,
    projectCode: null,

    // Matched projects.
    projectInSheet,
    projectInSystem: null,

    // Missing identifiers.
    isMissingClientName: true,
    isMissingProjectCode: true,
    isMissingProjectId: true,
  }

  return { ...defaultResult, ...overrides }
}

const setDifferences = (projectInSheet, projectInSystem, differences) => {
  // Compare project type
  if (projectInSheet.projectType.trim() !== (projectInSystem.projectType || '').trim()) {
    differences.push({
      field: 'projectType',
      sheetValue: projectInSheet.projectType,
      systemValue: projectInSystem.projectType,
    })
  }

  // Compare status
  if (projectInSheet.projectStatus.trim() !== (projectInSystem.projectStatus || '').trim()) {
    differences.push({
      field: 'projectStatus',
      sheetValue: projectInSheet.projectStatus,
      systemValue: projectInSystem.projectStatus,
    })
  }

  // Compare dates (if present in both)
  if (projectInSheet.startDate && projectInSystem.startDate) {
    const sheetDate = new Date(projectInSheet.startDate).toISOString().split('T')[0]
    const systemDate = new Date(projectInSystem.startDate).toISOString().split('T')[0]

    if (sheetDate !== systemDate) {
      differences.push({
        field: 'startDate',
        sheetValue: projectInSheet.startDate,
        systemValue: projectInSystem.startDate,
      })
    }
  }

  // Additional field comparisons can be added here following the same pattern.

  return differences
}

// eslint-disable-next-line arrow-body-style
const getProjectsInSystemOnly = (projectsInSystem) => {
  return projectsInSystem.filter((projectInSystem) => {
    const { isInSheet, isInSystem } = projectInSystem
    return isInSheet === false && isInSystem === true
  })
}

// eslint-disable-next-line arrow-body-style
const getProjectsInSheetOnly = (projectsInSheet) => {
  return projectsInSheet.filter((projectInSheet) => {
    const { isInSheet, isInSystem } = projectInSheet
    return isInSystem === false && isInSheet === true
  })
}

// eslint-disable-next-line arrow-body-style
const getProjectsInSheetAndSystem = (projectsInSheet) => {
  return projectsInSheet.filter((projectInSheet) => {
    const { isInSheetAndSystem } = projectInSheet
    return isInSheetAndSystem === true
  })
}

// eslint-disable-next-line arrow-body-style
const getProjectsWithDifferences = (projectsInSheetAndSystem) => {
  return projectsInSheetAndSystem.filter((m) => m.differences.length > 0)
}

const compareProjectsByExactMatch = (args) => {
  const {
    projectInSheet,
    projectInSystem,

    sheetProjectCode = String(projectInSheet.projectCode).trim(),
    sheetProjectId = String(projectInSheet.projectId).trim(),

    isMissingClientName,
    isMissingProjectCode,
    isMissingProjectId,
  } = args

  // Update the system project's source indicators.
  updateProjectInSystem(projectInSystem, {
    isInSheet: true,
    isInSheetAndSystem: true,
  })

  // Compare fields to find differences between the two projects.
  const differences = []

  // Populate differences array.
  setDifferences(projectInSheet, projectInSystem, differences)

  // Calculate total differences.
  const totalDifferences = differences.length

  // Determine the matching method.
  const shareProjectId = !isMissingProjectId && projectInSystem.projectId === sheetProjectId
  const matchBy = shareProjectId ? 'projectId' : 'projectCode'

  // Return the matched projects with additional info.
  return buildResultOfComparison(projectInSheet, {
    // Comparison info.
    matchBy,

    // Diff info.
    differences,
    totalDifferences,

    // Source info.
    isInSheet: true,
    isInSystem: true,
    isInSheetAndSystem: true,

    // Matched properties.
    projectId: sheetProjectId,
    projectCode: sheetProjectCode,

    // Matched projects.
    projectInSheet,
    projectInSystem,

    // Missing identifiers.
    isMissingClientName,
    isMissingProjectCode,
    isMissingProjectId,
  })
}

/**
 * Performs exact matching between spreadsheet projects and system projects using projectCode
 * @param {Array} sheetProjectsArray - Projects from Google Sheets
 * @param {Array} systemProjectsArray - Projects from the databases.
 * @returns {Object} Comparison results
 */
const compareProjects = (sheetProjectsArray, systemProjectsArray) => {
  const totalProjectsInSystem = systemProjectsArray.length
  const totalProjectsInSheet = sheetProjectsArray.length

  const systemProjectMap = {}

  // Keep projectsInSystem structure simple to prevent circular references in projectInSheet objects.
  const projectsInSystem = systemProjectsArray.map((projectInSystem) => {
    // Initialize system project's source indicators.
    setupProjectInSystem(projectInSystem)

    // Build system project map for quick lookups.
    createSystemProjectMap(projectInSystem, systemProjectMap)

    return projectInSystem
  })

  // Loop through spreadsheet projects and find matches in the system.
  const projectsInSheet = sheetProjectsArray.map((projectInSheet) => {
    // If the row has no identifiers, update relevant properties and return.
    if (!projectInSheet.projectId && !projectInSheet.projectCode && !projectInSheet.clientName) {
      return buildResultOfComparison(projectInSheet)
    }

    const sheetClientName = String(projectInSheet.clientName).trim()
    const sheetProjectCode = String(projectInSheet.projectCode).trim()
    const sheetProjectId = String(projectInSheet.projectId).trim()

    const isMissingClientName = !sheetClientName
    const isMissingProjectId = !sheetProjectId
    const isMissingProjectCode = !sheetProjectCode

    const getMatchByProjectCode = () => !isMissingProjectCode && systemProjectMap[sheetProjectCode]
    const getMatchByProjectId = () => !isMissingProjectId && systemProjectMap[sheetProjectId]

    // Match by projectId. Fallback to projectCode.
    const projectInSystem = getMatchByProjectId() || getMatchByProjectCode()

    if (projectInSystem) {
      // Determine differences between sheet and system projects.
      return compareProjectsByExactMatch({
        projectInSheet,
        projectInSystem,

        isMissingClientName,
        isMissingProjectCode,
        isMissingProjectId,
      })
    }

    // Fallback to client name matching if no exact match found.
    const projectByClientName = getMatchByClientName({
      isMissingClientName,
      sheetClientName,
      systemProjectsArray,
    })

    if (projectByClientName) {
      // Suggest the matched project's identifiers.
      return buildResultOfComparison(projectInSheet, {
        // Comparison info.
        matchBy: 'clientName',

        // Diff info.
        differences: [],
        totalDifferences: false,

        // Source info.
        isInSheet: true,
        isInSystem: true,
        isInSheetAndSystem: true,

        // Matched properties.
        projectId: sheetProjectId,
        projectCode: sheetProjectCode,

        // Matched projects.
        projectInSheet,
        projectInSystem: null,

        // Missing identifiers.
        missingClientName: isMissingClientName,
        missingProjectCode: isMissingProjectCode,
        missingProjectId: isMissingProjectId,
      })
    }

    // No match found in the system.
    return buildResultOfComparison(projectInSheet, {
      // Comparison info.
      matchBy: 'none',

      // Diff info.
      differences: [],
      totalDifferences: 0,

      // Source info.
      isInSheet: true,
      isInSystem: false,
      isInSheetAndSystem: false,

      // Matched properties.
      projectId: sheetProjectId,
      projectCode: sheetProjectCode,

      // Matched projects.
      projectInSheet,
      projectInSystem: null,

      // Missing identifiers.
      missingClientName: isMissingClientName,
      missingProjectCode: isMissingProjectCode,
      missingProjectId: isMissingProjectId,
    })
  })

  // Group projects by their presence in the sheet, the system, or both.
  const projectsInSystemOnly = getProjectsInSystemOnly(projectsInSystem, projectsInSheet)
  const projectsInSheetOnly = getProjectsInSheetOnly(projectsInSheet)
  const projectsInSheetAndSystem = getProjectsInSheetAndSystem(projectsInSheet)
  const projectsWithDifferences = getProjectsWithDifferences(projectsInSheetAndSystem)

  // Return view data for rendering.
  return {
    // Project groups.
    projectsInSheet,
    projectsInSystem,
    projectsInSheetAndSystem,

    projectsInSystemOnly,
    projectsInSheetOnly,

    projectsWithDifferences,

    // Summary of project counts.
    summary: {
      // Top-level totals.
      totalProjectsInSheet,
      totalProjectsInSystem,

      // Matched totals.
      totalProjectsInSheetAndSystem: projectsInSheetAndSystem.length,
      totalProjectsWithDifferences: projectsWithDifferences.length,

      // Totals for project groups.
      totalProjectsInSheetOnly: projectsInSheetOnly.length,
      totalProjectsInSystemOnly: projectsInSystemOnly.length,
    },
  }
}

const projectSyncActions = {
  /**
   * Fetches data from Google Sheets
   * @returns {Promise<Array>} Spreadsheet data rows
   */
  async fetchSpreadsheetData() {
    const sheetId = process.env.VUE_APP_PROJECT_SYNC_SHEET_ID

    if (!sheetId) {
      throw new Error('VUE_APP_PROJECT_SYNC_SHEET_ID is not configured')
    }

    try {
      // Use the googleAuth utility which handles token management automatically
      const data = await fetchSheetData(sheetId, 'Sheet1!A:Z')
      const rows = data.values || []

      if (rows.length === 0) {
        return []
      }

      // @note Configure Headers and Data Rows
      // Reflects the spreadsheet's row structure.
      // Headers are on row 4 (index 3), and data starts from row 5 (index 4). If this changes in the spreadsheet, update the indices here accordingly.
      // @todo We may want to make the header and data row ranges a dynamic or configurable value.
      const headers = rows[3]
      const dataRows = rows.slice(4)

      // Map each row to our project structure
      const mappedRows = dataRows.map((row) => mapSpreadsheetRow(row, headers))

      return mappedRows
    } catch (error) {
      logger.error('Error fetching spreadsheet data:', error)
      throw error
    }
  },

  /**
   * Fetches all projects from Firestore and API
   * @returns {Promise<Array>} System projects with enhanced data
   */
  async fetchSystemProjects() {
    try {
      // Fetch projects from API (includes more fields than Firestore)
      const projectsResponse = await api.getProjects()
      const projects = projectsResponse?.content || []

      // Enhance projects with Firestore data if needed
      const firestoreProjects = await firecollection.collection('project')

      // Merge API and Firestore data
      const enhancedProjects = projects.map((apiProject) => {
        const firestoreProject = firestoreProjects.find((fp) => fp.docId === apiProject.projectId)
        return {
          ...apiProject,
          ...firestoreProject,
          // Ensure we have the key identifiers
          projectId: apiProject.projectId || firestoreProject?.projectId,
          projectCode: apiProject.projectCode || firestoreProject?.projectCode,
          projectName: apiProject.projectName || firestoreProject?.projectName,
          clientName: apiProject.clientName || '',
        }
      })

      return enhancedProjects
    } catch (error) {
      logger.error('Error fetching system projects:', error)
      throw error
    }
  },

  /**
   * Fetches client data for name-to-code mapping
   * @returns {Promise<Array>} Client data
   */
  async fetchClients() {
    try {
      const clientsResponse = await api.getClients()
      return clientsResponse?.content || []
    } catch (error) {
      logger.error('Error fetching clients:', error)
      throw error
    }
  },

  /**
   * Main action to perform project comparison
   * @param {Object} context - Vuex context
   * @returns {Promise<Object>} Comparison results
   */
  async compareProjectData({ dispatch }) {
    try {
      // Fetch data from all sources in parallel
      // Note: fetchSpreadsheetData now handles token management internally
      const [projectsInSheet, projectsInSystem] = await Promise.all([
        dispatch('fetchSpreadsheetData'),
        dispatch('fetchSystemProjects'),
      ])

      // Perform comparison
      return compareProjects(projectsInSheet, projectsInSystem)
    } catch (error) {
      logger.error('Error comparing project data:', error)

      throw error
    }
  },

  /**
   * Gets the user's Google OAuth access token
   * @returns {Promise<string>} Access token
   */
  async getGoogleAccessToken() {
    try {
      const { fireauth } = await import('src/store/firebase')
      const currentUser = fireauth.getCurrentUser()

      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      // Get the access token from the user's credential
      const token = await currentUser.getIdToken(true)

      // Note: For Google Sheets API, we need the OAuth access token, not the Firebase ID token
      // We'll need to get this from the authentication result
      // This might require storing it when the user signs in
      return token
    } catch (error) {
      logger.error('Error getting access token:', error)
      throw error
    }
  },

  /**
   * Writes project codes back to the spreadsheet
   * @param {Object} context - Vuex context
   * @param {Object} payload - Contains matched projects to write back
   * @param {Array} payload.projectsInSheetAndSystem - Array of matched project objects
   * @param {number} payload.startRow - Row number to start writing (1-based, typically 2 for first data row)
   * @returns {Promise<Object>} Write result
   */
  async writeProjectCodesToSheet(context, { projectsInSheetAndSystem, startRow = 2 }) {
    const sheetId = process.env.VUE_APP_PROJECT_SYNC_SHEET_ID

    if (!sheetId) {
      throw new Error('VUE_APP_PROJECT_SYNC_SHEET_ID is not configured')
    }

    try {
      // Get all spreadsheet data first to determine row positions
      const data = await fetchSheetData(sheetId, 'Sheet1!A:Z')
      const rows = data.values || []

      if (rows.length === 0) {
        throw new Error('Spreadsheet is empty')
      }

      const headers = rows[0]

      // Find the Project Code column index (assuming it's column B, index 1)
      // You can make this dynamic based on header names
      const projectCodeColIndex = headers.findIndex((h) => h === 'Project Code')
      const projectIdColIndex = headers.findIndex((h) => h === 'Project ID')

      if (projectCodeColIndex === -1) {
        throw new Error('Project ID column not found in spreadsheet. Please add a "Project ID" column.')
      }

      // Build updates array
      const updates = []

      // For each matched project, update the corresponding row
      rows.slice(1).forEach((row, index) => {
        // We calculate index+2 because we must +1 for 0-based to 1-based,
        // then we +1 for the spreadsheet's header row.
        const rowNumber = index + 2

        // Get projectId from the sheet row.
        const projectIdFromSheet = (row[projectIdColIndex] || '').toString().trim()

        if (!projectIdFromSheet) {
          return // Skip rows without projectId
        }

        // Find matching project by projectId or projectCode
        const matchedProject = projectsInSheetAndSystem.find((m) => {
          const matchingProjectId = (m.projectInSheet.projectId || '').toString().trim()
          const matchingProjectCode = (m.projectInSheet.projectCode || '').toString().trim()

          return matchingProjectId === projectIdFromSheet || matchingProjectCode === projectIdFromSheet
        })

        if (matchedProject) {
          // Prepare the row update
          const updateRow = [...row] // Clone existing row
          updateRow[projectCodeColIndex] = matchedProject.projectInSystem.projectCode || ''

          if (projectIdColIndex !== -1) {
            updateRow[projectIdColIndex] = matchedProject.projectInSystem.projectId || ''
          }

          updates.push({
            rowNumber,
            values: updateRow,
          })
        }
      })

      if (updates.length === 0) {
        return {
          success: true,
          updatedRows: 0,
          message: 'No rows to update',
        }
      }

      // Write all updates at once using batch update
      const range = `Sheet1!A${startRow}:${String.fromCharCode(65 + headers.length - 1)}${startRow + rows.length - 2}`
      const values = updates.map((u) => u.values)

      await writeSheetData(sheetId, range, values, 'USER_ENTERED')

      logger.log(`Successfully wrote ${updates.length} project codes to spreadsheet`)

      return {
        success: true,
        updatedRows: updates.length,
        message: `Updated ${updates.length} rows with project codes`,
      }
    } catch (error) {
      logger.error('Error writing project codes to sheet:', error)
      throw error
    }
  },

  /**
   * Writes selected system projects as new rows to the spreadsheet
   * @param {Object} context - Vuex context
   * @param {Object} payload - Contains projects to add
   * @param {Array} payload.projects - Array of system project objects to add to sheet
   * @returns {Promise<Object>} Write result
   */
  async writeSystemProjectsToSheet(context, { projects }) {
    const sheetId = process.env.VUE_APP_PROJECT_SYNC_SHEET_ID

    if (!sheetId) {
      throw new Error('VUE_APP_PROJECT_SYNC_SHEET_ID is not configured')
    }

    if (!projects || projects.length === 0) {
      return {
        success: true,
        addedRows: 0,
        message: 'No projects to add',
      }
    }

    try {
      // Get current spreadsheet data to find the next empty row
      const data = await fetchSheetData(sheetId, 'Sheet1!A:Z')
      const rows = data.values || []

      if (rows.length === 0) {
        throw new Error('Spreadsheet is empty - cannot determine structure')
      }

      const headers = rows[0]
      const nextRow = rows.length + 1 // Next empty row (1-based)

      // Map system projects to spreadsheet row format
      const newRows = projects.map((project) => {
        const row = new Array(headers.length).fill('') // Empty array matching header length

        // Map fields based on header names
        headers.forEach((header, index) => {
          switch (header) {
            case 'Client':
              row[index] = project.clientName || ''
              break
            case 'Project Code':
              row[index] = project.projectCode || ''
              break
            case 'Project ID':
              row[index] = project.projectId || ''
              break
            case 'Project Type':
              row[index] = project.projectType || ''
              break
            case 'Status':
              row[index] = project.projectStatus || ''
              break
            case 'Project Start':
              row[index] = project.startDate || ''
              break
            case 'Projected Launch Date':
              row[index] = project.launchDateEstimate || ''
              break
            case 'Actual Launch Date':
              row[index] = project.launchDateActual || ''
              break
            case 'Project Close':
              row[index] = project.endDate || ''
              break
            // Add more field mappings as needed
            default:
              // Leave empty for unmapped columns
              break
          }
        })

        return row
      })

      // Write new rows to the spreadsheet
      const range = `Sheet1!A${nextRow}:${String.fromCharCode(65 + headers.length - 1)}${nextRow + newRows.length - 1}`

      await writeSheetData(sheetId, range, newRows, 'USER_ENTERED')

      logger.log(`Successfully added ${newRows.length} projects to spreadsheet`)

      return {
        success: true,
        addedRows: newRows.length,
        message: `Added ${newRows.length} project${newRows.length > 1 ? 's' : ''} to spreadsheet`,
      }
    } catch (error) {
      logger.error('Error writing system projects to sheet:', error)
      throw error
    }
  },
}

export default projectSyncActions
